import { NavLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Building2, MapPin, Tags, Package, 
  Star, Settings, Menu, X, Home, LogOut
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const navItems = [
  { path: '/pasteladmin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/pasteladmin/listings', icon: Building2, label: 'İşletmeler' },
  { path: '/pasteladmin/cities', icon: MapPin, label: 'Şehirler' },
  { path: '/pasteladmin/categories', icon: Tags, label: 'Kategoriler' },
  { path: '/pasteladmin/packages', icon: Package, label: 'Paketler' },
  { path: '/pasteladmin/showcase', icon: Star, label: 'Vitrin' },
  { path: '/pasteladmin/settings', icon: Settings, label: 'Ayarlar' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { admin, logout, isAuthenticated, loading } = useAuth();

  // Show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/pasteladmin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-white/10 transform transition-transform lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-semibold">
                <span className="text-foreground">Firma</span>
                <span className="text-primary">Burada</span>
              </h1>
              <p className="text-muted-foreground text-xs mt-1">Yönetim Paneli</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Admin info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-sm font-medium">{admin?.username}</p>
            <p className="text-xs text-muted-foreground capitalize">{admin?.role}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors
                  ${isActive 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }
                `}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Bottom actions */}
          <div className="p-4 border-t border-white/10 space-y-1">
            <NavLink
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            >
              <Home className="w-5 h-5" />
              Siteye Dön
            </NavLink>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              Çıkış Yap
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-white/10 flex items-center px-6 gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <h2 className="font-heading text-lg font-semibold">
            {navItems.find(item => 
              item.exact 
                ? location.pathname === item.path 
                : location.pathname.startsWith(item.path) && item.path !== '/pasteladmin'
            )?.label || 'Dashboard'}
          </h2>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
